import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { standupApi } from "@/lib/standup-api";
import DashboardSidebar from "@/components/DashboardSidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Check, ArrowLeft, ArrowRight, ArrowSquareOut, Sparkle, Star, Question,
  CaretLeft, CaretRight, Copy, EnvelopeSimple, ShieldCheck, Lightning, Cube,
  X as XIcon, Plus,
} from "@phosphor-icons/react";
import { toast } from "sonner";

// /standup/new — Frames 1.4, 1.5, 1.6.
//
// The wizard owns its own state across the 3 steps. We POST to /api/standup
// only at the end of Step 3 (Finish Setup) so abandoning the wizard doesn't
// leak half-baked standup definitions into the user's Manage list.

const DEFAULT_QUESTIONS = [
  { id: "q1", text: "What are your blockers or challenges?", required: true, type: "text" },
  { id: "q2", text: "What did you work on this week or what's In Progress?", required: true, type: "text" },
  { id: "q3", text: "What were your goals for today?", required: true, type: "text" },
  { id: "q4", text: "What help or collaboration do you need?", required: false, type: "text" },
  { id: "q5", text: "How's your general mood (use an emoji)?", required: false, type: "text" },
];

export default function StandUpSetup() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [collapsed, setCollapsed] = useState(false);
  const [step, setStep] = useState(1);

  // Top-level config the wizard mutates across steps
  const [config, setConfig] = useState(() => {
    const linkedProject = params.get("linked_project");
    const linkedTask = params.get("linked_task");
    return {
      name: params.get("name") || "",
      description: "",
      cadence: params.get("cadence") || "daily",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      recurrence: { time: "08:00", weekdays: ["Mo", "Tu"], interval_n: 2, interval_unit: "weeks" },
      background_id: "bg-03",
      template_id: null,
      questions: DEFAULT_QUESTIONS,
      participant_behaviours: { allow_edit_after_submit: true, allow_realtime_sharing: true },
      host_features: {
        enable_avg_time_display: true,
        enable_meeting_summary: false,
        allow_avg_time_suggestions: false,
        allow_intro_audio: false,
      },
      linked_task: linkedProject && linkedTask ? { project_id: linkedProject, task_id: linkedTask } : null,
    };
  });

  useEffect(() => {
    if (!authLoading && !user) navigate("/login", { replace: true });
  }, [authLoading, user, navigate]);

  function updateConfig(patch) {
    setConfig((c) => ({ ...c, ...patch }));
  }

  if (authLoading || !user) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen flex bg-white" data-testid="standup-setup">
      <DashboardSidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((v) => !v)}
        user={user}
        onNewProject={() => navigate("/dashboard")}
        onOpenMilestones={() => navigate("/dashboard")}
        onOpenSupport={() => navigate("/dashboard")}
        activeItem="standup"
      />
      <main className="flex-1 px-6 py-10 overflow-y-auto bg-[#FAFAFC]">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between">
            <button
              type="button"
              onClick={() => navigate("/standup")}
              className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1"
              data-testid="back-to-home"
            >
              <ArrowLeft size={12} weight="bold" /> Back to Standup Home
            </button>
            <div className="inline-flex items-center gap-2 rounded-full bg-white border border-violet-100 px-3 py-1 text-[11px] font-semibold tracking-wide text-slate-700">
              <Sparkle size={11} weight="fill" className="text-violet-500" />
              Set up Standup
            </div>
            <div className="w-[160px]" />
          </div>

          <StepProgress step={step} />

          {step === 1 && (
            <StepOne onPick={(path) => {
              if (path === "quick") quickLaunch(config, navigate);
              else if (path === "custom") setStep(2);
              else if (path === "last") quickLaunch(config, navigate);  // P5 will load last config
            }} />
          )}
          {step === 2 && (
            <StepTwo
              config={config}
              updateConfig={updateConfig}
              onBack={() => setStep(1)}
              onNext={() => setStep(3)}
            />
          )}
          {step === 3 && (
            <StepThree
              config={config}
              onBack={() => setStep(2)}
            />
          )}
        </div>
      </main>
    </div>
  );
}

async function quickLaunch(config, navigate) {
  // Create the standup with defaults, create a session, route into the loader.
  try {
    const res = await standupApi.create({
      name: config.name || "Quick Standup",
      cadence: config.cadence || "daily",
      questions: config.questions,
    });
    const sess = await standupApi.createSession(res.data.standup_id);
    navigate(`/standup/session/${sess.data.session_id}/launch`);
  } catch (e) {
    toast.error(e?.response?.data?.detail || "Could not launch standup");
  }
}

function StepProgress({ step }) {
  return (
    <div className="mt-10 flex items-center justify-center" data-testid="step-progress">
      {[1, 2, 3].map((n, i) => (
        <div key={n} className="flex items-center">
          <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase mr-2">Step {n}</div>
          <div
            className={`h-7 w-7 grid place-items-center rounded-full ${
              n < step ? "bg-emerald-500 text-white" :
              n === step ? "bg-emerald-500 text-white ring-4 ring-emerald-100" :
              "bg-slate-100 text-slate-400"
            }`}
            data-testid={`step-dot-${n}`}
          >
            {n < step || n === step ? <Check size={12} weight="bold" /> : null}
          </div>
          {i < 2 && (
            <div className={`mx-3 h-[2px] w-24 ${n < step ? "bg-emerald-500" : "bg-slate-200"}`} />
          )}
        </div>
      ))}
    </div>
  );
}

// -------------------- STEP 1 --------------------

function StepOne({ onPick }) {
  return (
    <div className="mt-12 grid sm:grid-cols-3 gap-5 max-w-4xl mx-auto" data-testid="step-1">
      <PathCard
        n="01"
        title="Quick Launch"
        emoji="🎉"
        tagline="Jump right into your standup"
        pills={["ASAP", "5 Questions", "Meeting Summary"]}
        cta="Start It!"
        onClick={() => onPick("quick")}
        ring="ring-2 ring-sky-500"
        testid="path-quick"
      >
        <div className="mt-4 rounded-lg bg-violet-50/70 border border-violet-100 p-4">
          <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Preview</div>
          <div className="mt-2 flex items-center gap-2 text-xs font-semibold text-slate-900">
            <span className="h-6 w-6 grid place-items-center rounded bg-teal-100">
              <Cube size={12} weight="bold" className="text-teal-700" />
            </span>
            [Default Standup Config]
          </div>
          <div className="mt-3 text-[10px] font-bold tracking-widest text-slate-500 uppercase">Standard Questions</div>
          <ol className="mt-1.5 space-y-1.5 text-xs text-slate-700">
            {DEFAULT_QUESTIONS.map((q, i) => (
              <li key={q.id} className="leading-tight">
                <span className="font-semibold mr-1">{String.fromCharCode(97 + i)})</span> {q.text}
              </li>
            ))}
          </ol>
        </div>
      </PathCard>
      <PathCard
        n="02"
        title="Custom Setup"
        emoji="🤩"
        tagline="Personalize the type of experience whether it be daily or retrospective."
        pills={["3 min", "Intro", "Tailored Meeting Agenda"]}
        cta="Customize It!"
        onClick={() => onPick("custom")}
        tone="amber"
        testid="path-custom"
      />
      <PathCard
        n="03"
        title="Last Launched"
        emoji="🧐"
        tagline="Standup beginning with the last saved version"
        pills={["ASAP", "Intro", "Tailored Meeting Agenda"]}
        cta="Launch It!"
        onClick={() => onPick("last")}
        tone="violet"
        testid="path-last"
      />
    </div>
  );
}

function PathCard({ n, title, emoji, tagline, pills, cta, onClick, children, ring = "", tone = "default", testid }) {
  const toneBg = {
    default: "bg-white",
    amber: "bg-gradient-to-br from-rose-50 via-amber-50 to-yellow-50",
    violet: "bg-gradient-to-br from-violet-50 via-sky-50 to-pink-50",
  }[tone];
  return (
    <div className={`relative rounded-2xl border border-slate-200 ${toneBg} p-5 ${ring}`} data-testid={testid}>
      <div className="text-[10px] font-bold tracking-widest text-slate-400">{n}</div>
      <div className="mt-3 flex items-start gap-2">
        <span className="text-2xl leading-none" aria-hidden="true">{emoji}</span>
        <div>
          <h3 className="text-base font-bold text-slate-900">{title}</h3>
          <p className="mt-0.5 text-xs text-slate-600 leading-snug">{tagline}</p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {pills.map((p) => (
          <span key={p} className="text-[10px] font-semibold rounded-full bg-white/70 backdrop-blur border border-slate-200 px-2 py-0.5 text-slate-700">
            {p}
          </span>
        ))}
      </div>
      {children}
      <Button
        onClick={onClick}
        className="mt-4 w-full h-9 bg-slate-950 hover:bg-slate-800 text-white text-xs font-semibold"
        data-testid={`${testid}-cta`}
      >
        {cta} <CaretRight size={11} weight="bold" className="ml-1" />
      </Button>
    </div>
  );
}

// -------------------- STEP 2 --------------------

function StepTwo({ config, updateConfig, onBack, onNext }) {
  const [templates, setTemplates] = useState([]);
  const [methodologyFilter, setMethodologyFilter] = useState("agile");
  const [backgrounds, setBackgrounds] = useState([]);
  const [editing, setEditing] = useState(null); // template being edited in right pane

  useEffect(() => {
    standupApi.templates().then((r) => {
      setTemplates(r.data.templates || []);
    }).catch(() => setTemplates([]));
    standupApi.backgrounds().then((r) => setBackgrounds(r.data.backgrounds || []));
  }, []);

  const descCount = (config.description || "").length;
  const overLimit = descCount > 200;
  const filteredTemplates = templates.filter((t) =>
    methodologyFilter === "all" ? true : t.methodology === methodologyFilter
  );

  function pickTemplate(tpl) {
    updateConfig({ template_id: tpl.template_id, questions: tpl.questions });
    toast.success(`Loaded ${tpl.name}`);
  }

  return (
    <div className="mt-10" data-testid="step-2">
      <div className="rounded-2xl bg-white border border-slate-200 p-8">
        {/* Description */}
        <Field label="Description">
          <Textarea
            value={config.description}
            onChange={(e) => updateConfig({ description: e.target.value })}
            placeholder="This meeting is to address our progress over the past week. It's a pulse check which will keep our team on top of your spirit."
            className="h-20 text-sm"
            data-testid="description-input"
          />
          {overLimit && (
            <div className="mt-1 text-right text-[11px] text-rose-500">
              · {descCount - 200} characters over the limit. Max 200 characters.
            </div>
          )}
        </Field>

        <Field label="Timezone">
          <Select value={config.timezone} onValueChange={(v) => updateConfig({ timezone: v })}>
            <SelectTrigger className="h-9 text-sm w-full" data-testid="timezone-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {["UTC", "America/New_York", "America/Chicago", "America/Los_Angeles", "Europe/London", "Asia/Tokyo"].map((tz) => (
                <SelectItem key={tz} value={tz}>{tz}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <Field label="Recurrence">
          <div className="grid grid-cols-3 gap-3">
            {["daily", "weekly", "custom"].map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => updateConfig({ cadence: c })}
                className={`text-left rounded-md border px-3 py-2 ${
                  config.cadence === c ? "border-violet-500 bg-violet-50/60" : "border-slate-200"
                }`}
                data-testid={`recurrence-${c}`}
              >
                <div className="text-xs font-semibold capitalize text-slate-900">{c}</div>
                <div className="text-[10px] text-slate-500">
                  {c === "daily" ? "Mon - Fri" : c === "weekly" ? "Once" : "Anytime"}
                </div>
              </button>
            ))}
          </div>
        </Field>

        {/* Recurrence detail row */}
        <div className="mt-2 grid grid-cols-6 gap-3 items-center text-xs text-slate-600">
          <Input
            type="time"
            value={config.recurrence.time}
            onChange={(e) => updateConfig({ recurrence: { ...config.recurrence, time: e.target.value } })}
            className="h-8 text-xs"
          />
          <Input
            type="number"
            min={1}
            value={config.recurrence.interval_n}
            onChange={(e) => updateConfig({ recurrence: { ...config.recurrence, interval_n: parseInt(e.target.value || "1", 10) } })}
            className="h-8 text-xs"
          />
          <div className="col-span-4 flex items-center gap-1.5 flex-wrap">
            {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => {
              const active = (config.recurrence.weekdays || []).includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  onClick={() => {
                    const set = new Set(config.recurrence.weekdays || []);
                    if (set.has(d)) set.delete(d); else set.add(d);
                    updateConfig({ recurrence: { ...config.recurrence, weekdays: Array.from(set) } });
                  }}
                  className={`h-7 w-7 rounded-md text-[11px] font-semibold ${
                    active ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500"
                  }`}
                  data-testid={`day-${d}`}
                >
                  {d}
                </button>
              );
            })}
          </div>
        </div>

        <Field label="Background">
          <div className="grid grid-cols-12 gap-2 mt-1">
            {backgrounds.slice(0, 24).map((b) => (
              <button
                key={b.slug}
                type="button"
                onClick={() => updateConfig({ background_id: b.slug })}
                className={`aspect-square rounded-lg border-2 transition-colors ${
                  config.background_id === b.slug ? "border-sky-500" : "border-transparent hover:border-slate-300"
                }`}
                style={{ background: hashGradient(b.slug) }}
                title={b.label}
                data-testid={`bg-${b.slug}`}
              />
            ))}
          </div>
        </Field>

        <Field label="Participant Behaviours">
          <div className="space-y-2">
            <CheckRow
              checked={config.participant_behaviours.allow_edit_after_submit}
              onChange={(v) => updateConfig({ participant_behaviours: { ...config.participant_behaviours, allow_edit_after_submit: v } })}
              label="Allow participants to update bullet points after submission"
              testid="behaviour-edit"
            />
            <CheckRow
              checked={config.participant_behaviours.allow_realtime_sharing}
              onChange={(v) => updateConfig({ participant_behaviours: { ...config.participant_behaviours, allow_realtime_sharing: v } })}
              label="Allow real-time sharing mode and contribution tracking"
              testid="behaviour-realtime"
            />
          </div>
        </Field>

        <Field label="Host Control Features">
          <div className="space-y-2">
            {[
              ["enable_avg_time_display", "Enable average time per task display"],
              ["enable_meeting_summary", "Enable standup meeting summary"],
              ["allow_avg_time_suggestions", "Allow Avg time per task suggestions"],
              ["allow_intro_audio", "Allow standup intro music & vibe at meeting launch"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between text-sm text-slate-700">
                <span>{label}</span>
                <Switch
                  checked={!!config.host_features[key]}
                  onCheckedChange={(v) => updateConfig({ host_features: { ...config.host_features, [key]: v } })}
                  data-testid={`feature-${key}`}
                />
              </div>
            ))}
          </div>
        </Field>

        <Field label="Stand Up Questions">
          <div className="flex flex-wrap items-center gap-2">
            {["all", "agile", "lean", "six_sigma", "waterfall", "safe", "custom"].map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMethodologyFilter(m)}
                className={`h-7 px-3 rounded-md text-[11px] font-semibold capitalize ${
                  methodologyFilter === m ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-600"
                }`}
                data-testid={`methodology-${m}`}
              >
                {m.replace("_", " ")}
              </button>
            ))}
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            {filteredTemplates.map((t) => (
              <button
                key={t.template_id}
                type="button"
                onClick={() => pickTemplate(t)}
                className={`text-left rounded-lg border p-4 transition-colors ${
                  config.template_id === t.template_id ? "border-violet-500 bg-violet-50/40" : "border-slate-200 hover:border-slate-300"
                }`}
                data-testid={`template-${t.template_id}`}
              >
                <div className="text-sm font-bold text-sky-600">{t.name}</div>
                <ol className="mt-2 space-y-1 text-xs text-slate-700">
                  {(t.questions || []).slice(0, 5).map((q, i) => (
                    <li key={q.id} className="leading-tight">
                      <span className="mr-1 text-slate-400">{i + 1}</span> {q.text}
                    </li>
                  ))}
                </ol>
              </button>
            ))}
          </div>
        </Field>
      </div>

      <div className="mt-6 flex items-center justify-end gap-3">
        <Button variant="outline" onClick={onBack} className="h-10 px-5" data-testid="step-2-back">
          Cancel
        </Button>
        <Button
          onClick={onNext}
          disabled={overLimit}
          className="h-10 px-6 bg-slate-950 hover:bg-slate-800 text-white"
          data-testid="step-2-next"
        >
          Next <ArrowRight size={13} weight="bold" className="ml-1" />
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mt-5">
      <label className="text-xs font-semibold text-slate-500">{label}</label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function CheckRow({ checked, onChange, label, testid }) {
  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <Checkbox checked={checked} onCheckedChange={onChange} data-testid={testid} />
      <span className="text-sm text-slate-700">{label}</span>
    </label>
  );
}

// -------------------- STEP 3 --------------------

function StepThree({ config, onBack }) {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [input, setInput] = useState("");
  const [autoJoin, setAutoJoin] = useState(false);
  const [linkUrl, setLinkUrl] = useState("");
  const [creating, setCreating] = useState(false);
  const [createdId, setCreatedId] = useState(null);

  function addEmail() {
    const v = input.trim();
    if (!v) return;
    setEmails((es) => [...es, v]);
    setInput("");
  }

  function removeEmail(idx) {
    setEmails((es) => es.filter((_, i) => i !== idx));
  }

  const validEmails = useMemo(() => emails.filter((e) => /\S+@\S+\.\S+/.test(e)), [emails]);

  async function finish() {
    setCreating(true);
    try {
      const create = await standupApi.create(config);
      const standupId = create.data.standup_id;
      setCreatedId(standupId);
      if (validEmails.length > 0) {
        const invite = await standupApi.invite(standupId, { emails: validEmails, auto_join: autoJoin });
        setLinkUrl(invite.data.magic_link?.url || "");
      }
      toast.success("Standup created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create standup");
    } finally {
      setCreating(false);
    }
  }

  async function startMeetingNow() {
    if (!createdId) {
      await finish();
    }
    if (createdId) {
      const sess = await standupApi.createSession(createdId);
      navigate(`/standup/session/${sess.data.session_id}/launch`);
    }
  }

  function copyLink() {
    if (!linkUrl) return;
    navigator.clipboard?.writeText(linkUrl);
    toast.success("Magic link copied");
  }

  return (
    <div className="mt-10 max-w-3xl mx-auto" data-testid="step-3">
      <div className="rounded-2xl bg-white border border-slate-200 p-8">
        <h2 className="text-xl font-bold text-slate-900">Invite your team</h2>
        <p className="mt-1 text-xs text-slate-500">Add the members you want to participate in this standup. You can add more later.</p>

        <div className="mt-6 grid sm:grid-cols-[1fr,260px] gap-6">
          {/* Left column */}
          <div>
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Instant Access Link</span>
                <label className="flex items-center gap-1.5 text-xs">
                  <Checkbox checked={autoJoin} onCheckedChange={setAutoJoin} data-testid="auto-join-toggle" />
                  <span className="text-slate-700 font-medium">Auto join Enabled</span>
                </label>
              </div>
              <div className="mt-2 flex items-center gap-3">
                <span className="h-8 w-8 rounded-md bg-teal-100 grid place-items-center">
                  <Lightning size={14} weight="fill" className="text-teal-700" />
                </span>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-slate-900">Magic Join Link</div>
                  <p className="text-[11px] text-slate-500">Anyone with this link can bypass the lobby and enter the meeting immediately.</p>
                </div>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Input value={linkUrl} readOnly placeholder="tgnt.co/j/8q2-vortex-standup" className="h-9 text-xs" data-testid="magic-link" />
                <Button onClick={copyLink} className="h-9 bg-sky-500 hover:bg-sky-600 text-white text-xs" data-testid="copy-link-btn">
                  <Copy size={11} weight="bold" className="mr-1" /> Copy Link
                </Button>
              </div>
            </div>

            <div className="mt-5 rounded-lg border border-slate-200 p-4">
              <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Invite Via Email</div>
              <div className="mt-2">
                <Input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addEmail(); } }}
                  placeholder="Enter Email Address..."
                  className="h-9 text-sm"
                  data-testid="invite-email-input"
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {emails.map((e, i) => {
                  const ok = /\S+@\S+\.\S+/.test(e);
                  return (
                    <span
                      key={i}
                      className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-[11px] font-medium ${
                        ok ? "bg-violet-100 text-violet-800" : "bg-rose-100 text-rose-700"
                      }`}
                    >
                      {e}
                      <button onClick={() => removeEmail(i)} className="opacity-70 hover:opacity-100">
                        <XIcon size={10} weight="bold" />
                      </button>
                    </span>
                  );
                })}
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-slate-500">
                <span>HINT: Paste a list of emails to auto-parse</span>
                <button type="button" onClick={addEmail} className="text-sky-600 hover:underline" data-testid="send-invites-btn">
                  Send invites ({validEmails.length})
                </button>
              </div>
            </div>
          </div>

          {/* Right column - room status */}
          <div className="rounded-lg border border-slate-200 p-4 self-start">
            <div className="text-[10px] font-bold tracking-widest text-slate-500 uppercase">Room Status</div>
            <div className="mt-3 space-y-2 text-xs">
              {validEmails.length === 0 ? (
                <div className="text-slate-400">Invitees will appear here as you add them.</div>
              ) : (
                validEmails.map((e) => (
                  <div key={e} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="h-6 w-6 rounded-md text-white text-[10px] font-bold grid place-items-center" style={{ background: hashGradient(e) }}>
                        {e[0].toUpperCase()}
                      </span>
                      <span className="text-slate-700 truncate max-w-[120px]">{e}</span>
                    </div>
                    <span className="text-[10px] font-semibold rounded bg-amber-100 text-amber-800 px-1.5 py-0.5">Pending</span>
                  </div>
                ))
              )}
            </div>
            <Button onClick={startMeetingNow} disabled={creating} className="mt-4 w-full h-9 bg-violet-600 hover:bg-violet-700 text-white text-xs" data-testid="start-now-btn">
              Start Meeting Now
            </Button>
            <button type="button" onClick={finish} disabled={creating} className="mt-2 w-full text-[11px] text-slate-500 hover:text-slate-900" data-testid="schedule-later-btn">
              Schedule For Later
            </button>

            <div className="mt-4 pt-4 border-t border-slate-100 flex items-start gap-2 text-[11px] text-slate-500">
              <ShieldCheck size={14} weight="fill" className="text-emerald-500 mt-0.5" />
              <p>Encryption is active. Magic link access will expire in 1 hour.</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <Button onClick={finish} disabled={creating} className="h-10 px-5 bg-violet-600 hover:bg-violet-700 text-white" data-testid="finish-setup-btn">
            Finish Setup
          </Button>
          <button type="button" onClick={() => navigate("/standup")} className="text-xs text-slate-500 hover:text-slate-900" data-testid="skip-for-now-btn">
            Skip for now
          </button>
        </div>
      </div>

      <div className="mt-4 flex items-center justify-start">
        <button onClick={onBack} className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1" data-testid="step-3-back">
          <CaretLeft size={12} weight="bold" /> Back to Step 2
        </button>
      </div>
    </div>
  );
}

function hashGradient(seed) {
  let h = 0;
  for (let i = 0; i < (seed || "").length; i++) h = (h * 31 + seed.charCodeAt(i)) & 0xfff;
  const a = h % 360;
  const b = (a + 60) % 360;
  return `linear-gradient(135deg, hsl(${a}, 70%, 55%), hsl(${b}, 65%, 60%))`;
}
